import {
  LEGACY_TOPIC_SOFT_BG,
  TOPIC_PALETTE,
  WEBINAR_TIMEZONE,
} from "../constants";
import {
  ApiHost,
  ApiHostWriteBody,
  ApiPost,
  ApiPostWriteBody,
  ApiTopic,
  ApiTopicWriteBody,
} from "../types/api";
import { HostSaveDto, SessionSaveDto, TopicSaveDto } from "../types/dto";
import {
  SessionAccess,
  SessionStatus,
  WebinarHost,
  WebinarSession,
  WebinarTopic,
} from "../types/models";
import { SessionFormState } from "../utils/sessionFormState";
import { deriveStartAt } from "../utils/deriveStartAt";

const FORMAT_TO_API: Record<string, string> = {
  "Talk + Q&A": "talk_qa",
  Workshop: "workshop",
  "Group consult": "group_consult",
  AMA: "ama",
};

const FORMAT_FROM_API: Record<string, string> = {
  talk_qa: "Talk + Q&A",
  workshop: "Workshop",
  group_consult: "Group consult",
  ama: "AMA",
};

const STATUS_SET = new Set<SessionStatus>([
  "draft",
  "scheduled",
  "live",
  "ended",
  "cancelled",
]);

/** Map common BE aliases → UI session status. */
const STATUS_ALIASES: Record<string, SessionStatus> = {
  draft: "draft",
  scheduled: "scheduled",
  published: "scheduled",
  live: "live",
  in_progress: "live",
  inprogress: "live",
  ongoing: "live",
  ended: "ended",
  completed: "ended",
  complete: "ended",
  finished: "ended",
  past: "ended",
  cancelled: "cancelled",
  canceled: "cancelled",
};

export function slugifyTopicName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return slug || "topic";
}

export function formatToApi(format: string): string {
  return FORMAT_TO_API[format] || "talk_qa";
}

export function formatFromApi(format?: string): string {
  if (!format) return "Talk + Q&A";
  return FORMAT_FROM_API[format] || format;
}

export function accessToApi(access: SessionAccess): boolean {
  return access === "members";
}

export function accessFromApi(isForPaidUserOnly?: boolean): SessionAccess {
  return isForPaidUserOnly ? "members" : "open";
}

export function softBgFromColor(color?: string): string {
  if (!color) return TOPIC_PALETTE[0].bg;
  const key = color.toLowerCase();
  const match = TOPIC_PALETTE.find((p) => p.fg.toLowerCase() === key);
  if (match) return match.bg;
  return LEGACY_TOPIC_SOFT_BG[key] || TOPIC_PALETTE[0].bg;
}

export function istPartsFromStartsAt(startsAt?: string | null): {
  date: string;
  time: string;
  start: number;
  startAt: string;
} {
  if (!startsAt) {
    return { date: "", time: "", start: 0, startAt: "" };
  }
  const start = Date.parse(startsAt);
  if (Number.isNaN(start)) {
    return { date: "", time: "", start: 0, startAt: "" };
  }

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: WEBINAR_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(start));

  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value || "00";

  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
    start,
    startAt: new Date(start).toISOString(),
  };
}

export function normalizeStatus(
  status?: string,
  opts?: { hasStart?: boolean }
): SessionStatus {
  if (!status || !String(status).trim()) {
    return opts?.hasStart ? "scheduled" : "draft";
  }
  const key = String(status)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (STATUS_ALIASES[key]) return STATUS_ALIASES[key];
  if (STATUS_SET.has(status as SessionStatus)) return status as SessionStatus;
  // Unknown BE label: prefer scheduled when a start exists so cancel/edit stay sensible.
  return opts?.hasStart ? "scheduled" : "draft";
}

/**
 * Refine scheduled/live posts from wall-clock when BE hasn't flipped status yet.
 * draft / cancelled / ended are left alone.
 */
export function deriveLifecycleStatus(
  status: SessionStatus,
  startsAtMs: number,
  durationMin: number,
  now = Date.now()
): SessionStatus {
  if (status === "draft" || status === "cancelled" || status === "ended") {
    return status;
  }
  if (!startsAtMs || Number.isNaN(startsAtMs)) return status;

  const endMs = startsAtMs + Math.max(1, durationMin || 45) * 60_000;
  if (now < startsAtMs) return "scheduled";
  if (now < endMs) return "live";
  return "ended";
}

/**
 * Host list/detail APIs sign avatarUrl. If a full signed URL was stored by mistake,
 * the backend double-wraps it under aztatva-prod-storage-container — unwrap here.
 */
export function normalizeHostAvatarUrl(
  avatarUrl?: string | null,
): string | undefined {
  if (!avatarUrl || typeof avatarUrl !== "string") return undefined;
  const trimmed = avatarUrl.trim();
  if (!trimmed) return undefined;

  const nestedMatch = trimmed.match(
    /\/aztatva-prod-storage-container\/(https%3A%2F%2F.+)$/i,
  );
  if (nestedMatch) {
    try {
      return decodeURIComponent(nestedMatch[1]);
    } catch {
      return trimmed;
    }
  }

  return trimmed;
}

export function mapApiHostToHost(api: ApiHost): WebinarHost {
  return {
    id: api._id,
    name: api.name || "",
    role: api.role || "",
    bio: api.bio || "",
    creds: Array.isArray(api.credentials) ? [...api.credentials] : [],
    photo: normalizeHostAvatarUrl(api.avatarUrl),
    phoneNumber: api.phoneNumber || undefined,
  };
}

export function mapApiTopicToTopic(api: ApiTopic): WebinarTopic {
  const fg = api.color || TOPIC_PALETTE[0].fg;
  return {
    id: api._id,
    name: api.name || "",
    fg,
    bg: softBgFromColor(fg),
    slug: api.slug,
  };
}

export function mapApiPostToSession(
  api: ApiPost,
  hostsById?: Map<string, WebinarHost>,
  topicsById?: Map<string, WebinarTopic>
): WebinarSession {
  const { date, time, start, startAt } = istPartsFromStartsAt(api.startsAt);
  const host =
    api.host != null
      ? mapApiHostToHost(api.host)
      : hostsById?.get(api.hostId || "");
  const topic =
    api.topic != null
      ? mapApiTopicToTopic(api.topic)
      : topicsById?.get(api.topicId || "");

  const backendStatus = normalizeStatus(api.status, {
    hasStart: Boolean(start),
  });
  return {
    id: api._id,
    title: api.title || "",
    oneLiner: api.oneLineDescription || "",
    fullDesc: api.fullDescription || "",
    takeaways: Array.isArray(api.takeaways) ? [...api.takeaways] : [],
    topicId: api.topicId || topic?.id || "",
    topicName: topic?.name || "",
    hostId: api.hostId || host?.id || "",
    hostName: host?.name || "",
    hostRole: host?.role || "",
    hostPhoto: host?.photo,
    date,
    time,
    timezone: WEBINAR_TIMEZONE,
    startAt: startAt || api.startsAt || "",
    start,
    mins: Number(api.durationMin) || 45,
    access: accessFromApi(api.isForPaidUserOnly),
    cohorts: [],
    lang: api.primaryLanguage || "English",
    format: formatFromApi(api.format),
    provider: api.streamingProvider || "GoodFlip Live",
    joinUrl: api.joinUrl || "",
    shortDeepLink:
      typeof api.shortDeepLink === "string" && api.shortDeepLink.trim()
        ? api.shortDeepLink.trim()
        : undefined,
    recurrence: api.recurrence !== 'none' ? api.recurrence : undefined,
    recEnd: api.recurrenceEndDate || undefined,
    backendStatus,
    status: deriveLifecycleStatus(
      backendStatus,
      start,
      Number(api.durationMin) || 45
    ),
    reminders: Number(api.remindedCount) || 0,
    series: api.seriesId || undefined,
    rescheduled: Boolean(api.isRescheduled),
    updatedAt: api.updatedAt || api.createdAt || new Date().toISOString(),
    cancelReason: api.cancelReason || undefined,
    cancelNote: api.cancelMemberNote || undefined,
  };
}

export function startsAtFromForm(
  date: string,
  time: string
): string | null {
  if (!date?.trim() || !time?.trim()) return null;
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return deriveStartAt(date, time).startAt;
}

export function mapFormToApiPostBody(form: SessionFormState): ApiPostWriteBody {
  return {
    title: form.title.trim(),
    oneLineDescription: form.oneLiner.trim(),
    fullDescription: form.fullDesc.trim(),
    takeaways: form.takeaways.map((t) => t.trim()).filter(Boolean),
    topicId: form.topicId || "",
    hostId: form.hostId || "",
    isForPaidUserOnly: accessToApi(form.access),
    startsAt: startsAtFromForm(form.date, form.time),
    timezone: WEBINAR_TIMEZONE,
    durationMin: Number(form.mins) || 45,
    format: formatToApi(form.format),
    primaryLanguage: form.lang || "English",
    streamingProvider: form.provider || "GoodFlip Live",
    joinUrl: form.joinUrl.trim(),
    recurrence: form.recurrence || "none",
    recurrenceEndDate:
      form.recurrence && form.recurrence !== "none"
        ? form.recEnd || null
        : null,
  };
}

export function mapSessionSaveDtoToApiBody(
  dto: SessionSaveDto
): ApiPostWriteBody {
  const body: ApiPostWriteBody = {
    title: dto.title.trim(),
    oneLineDescription: dto.oneLiner.trim(),
    fullDescription: dto.fullDesc.trim(),
    takeaways: [...dto.takeaways],
    topicId: dto.topicId || "",
    hostId: dto.hostId || "",
    isForPaidUserOnly: accessToApi(dto.access),
    startsAt: dto.date && dto.time ? startsAtFromForm(dto.date, dto.time) : null,
    timezone: WEBINAR_TIMEZONE,
    durationMin: Number(dto.mins) || 45,
    format: formatToApi(dto.format),
    primaryLanguage: dto.lang || "English",
    streamingProvider: dto.provider || "GoodFlip Live",
    joinUrl: (dto.joinUrl || "").trim(),
    recurrence: dto.recurrence || "none",
    recurrenceEndDate:
      dto.recurrence && dto.recurrence !== "none"
        ? dto.recEnd || null
        : null,
  };
  if (dto.isRescheduled) {
    body.isRescheduled = true;
  }
  return body;
}

export function mapHostSaveDtoToApiBody(dto: HostSaveDto): ApiHostWriteBody {
  const body: ApiHostWriteBody = {};
  if (dto.name !== undefined) body.name = dto.name.trim();
  if (dto.role !== undefined) body.role = dto.role.trim();
  if (dto.bio !== undefined) body.bio = (dto.bio || "").trim();
  if (dto.creds !== undefined) body.credentials = [...dto.creds];
  if (dto.phoneNumber !== undefined) {
    body.phoneNumber = (dto.phoneNumber || "").trim();
  }
  if (dto.photo !== undefined) {
    body.avatarUrl = dto.photo || undefined;
  }
  return body;
}

export function mapTopicSaveDtoToApiBody(dto: TopicSaveDto): ApiTopicWriteBody {
  const name = dto.name.trim();
  return {
    name,
    slug: dto.slug || slugifyTopicName(name),
    color: dto.color || TOPIC_PALETTE[0].fg,
  };
}
